import { Info } from "lucide-react";
import React from "react";

export interface ContactDetails {
  name: string;
  email: string;
  phone: string;
}

export interface OnboardingFormData {
  clientName: string;
  dateSubmitted: string;

  // Section 1
  pilotCompaniesConfirmed: boolean;
  numberOfPilotCompanies: string;
  childOrganizations: { name: string; domain: string; contactEmail: string }[];
  includeEmployeeLifecycle: boolean; // Retaining for backwards compatibility or logic if needed

  // Step A — Product bundle selector
  productsSelected: string[];
  tierSelected: string;
  bundleDiscountApplied: string;

  // Step B — Three contacts
  l0Contact: ContactDetails;
  financeContact: ContactDetails;
  projectManagerContact: ContactDetails;

  // Step C — Company details
  legalCompanyName: string;
  legalEntityName: string;
  brandDisplayName: string;
  headOfficeAddress: string;
  industrySector: string;
  estimatedEmployeeCount: string;
  parentDomain: string;
  crossBorderProcessing: boolean;
  crossBorderJurisdictions: string;
  NIN: string;
  TIN: string;
  cacRegNumber?: string;

  // Section 3
  totalPhase1Users: string;
  roleBasedAccessRequired: boolean;
  roleBasedAccessDetails: string;
  thirdPartyAccessDeclared: boolean;
  thirdPartyAccessDetails: string;

  // Section 4
  billingContactName: string;
  billingContactEmail: string;
  billingContactPhone: string;
  preferredInvoicingEmail: string;
  paymentMethodPreference: string;
  specialInvoicingInstructions: string;
  accessLevel?: string;
  // Section 5
  personnel: {
    jobTitle: string;
    firstName: string;
    lastName: string;
    email: string;
    accessLevel: number;
  }[];

  // Section 6
  existingHRIS: string;
  hrisIntegrationRequired: boolean;
  hrisIntegrationDetails: string;
  jobPostingPlatforms: string;
  emailProvider: string;
  smsGatewayProvider: string;
  biometricIntegrationRequired: boolean;
  biometricDetails: string;

  // Section 7
  platformDisplayName: string;
  primaryBrandColor: string;
  customDomain: string;
  complianceRules: string;

  // Section 8
  additionalDeploymentNotes: string;

  // cacRegNumber: string;
  jurisdictionDetected: string;

  // Step D — Tier & seat configuration
  namedUserSeats: string;
  additionalSeatsRequired: string;
  multiEntityFlag: boolean;
  numberOfMemberEntities: string;
  concurrentLicensingRequired: boolean;
  numberOfConcurrentSessions: string;

  // Step E — Lifecycle phases
  lifecyclePhase1: boolean;
  lifecyclePhases2to7: string[];
  lifecyclePhase8Custom: string;

  // Step F — Billing
  billingModel?: string;
  currency: string;
  paymentMethod?: string;

  // Step G — Delivery timeline
  targetGoLiveDate: string;
  deliveryUrgency: string;

  // Step I — Pre-live checklist
  trainingCompletionRecord: boolean;
  uatSignOff: boolean;
  dataMigrationVerified: boolean;
  integrationsTested: boolean;
  clientL0GoLiveReadinessDeclaration: boolean;

  // Section 9
  scopeConfirmed: boolean;
  expansionAcknowledged: boolean;
  unauthorizedBreachAcknowledged: boolean;
  dataComplianceConfirmed: boolean;

  // Section 10 — Signature & submit
  electronicAgreement: boolean;
  authorizedFullName: string;
  authorizedTitle: string;
  authorizedDate: string;
}

interface OnboardingFormContentProps {
  formData: OnboardingFormData;
  onFormChange: (field: keyof OnboardingFormData, value: any) => void;
  onChildOrgChange: (
    index: number,
    field: keyof OnboardingFormData["childOrganizations"][0],
    value: string,
  ) => void;
  // Step B nested handlers
  onContactChange: (
    contactType: "l0Contact" | "financeContact" | "projectManagerContact",
    field: keyof ContactDetails,
    value: string,
  ) => void;
  addChildOrg: () => void;
  removeChildOrg: (index: number) => void;
  onStakeholderChange: (
    index: number,
    field: keyof OnboardingFormData["personnel"][0],
    value: any,
  ) => void;
  addStakeholder: () => void;
  removeStakeholder: (index: number) => void;
  logoFile: File | null;
  logoPreview: string | null;
  onLogoUpload: (file: File) => void;
  onRemoveLogo: () => void;

  // Step E nested handlers
  onPhaseToggle: (phase: string) => void;
  onProductToggle: (product: string) => void;

  // File Uploads
  cacFile: File | null;
  onCacUpload: (file: File) => void;
  dataMigrationFile: File | null;
  onDataMigrationUpload: (file: File) => void;
  // companyProfileFile: File | null;
  // onCompanyProfileUpload: (file: File) => void;

  // Legacy props (keeping optional to avoid breaking anything that might try to pass them)
  // logoFile?: File | null;
  // logoPreview?: string | null;
  // onLogoUpload?: (file: File) => void;
  // onRemoveLogo?: () => void;
  tinFile?: File | null;
  ninFile?: File | null;
  onTinUpload?: (file: File) => void;
  onNinUpload?: (file: File) => void;
  companyProfileFile?: File | null;
  onCompanyProfileUpload?: (file: File) => void;
  childOrganizations?: any[];
  // onChildOrgChange?: any;
  // addChildOrg?: any;
  // removeChildOrg?: any;
  personnel?: any[];
  // onStakeholderChange?: any;
  // addStakeholder?: any;
  // removeStakeholder?: any;
}

const inputClass =
  "w-full px-3 py-2 flex items-center justify-between border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors";

const labelClass = "block text-sm font-medium text-gray-700 mb-1";

const checkboxRowClass = "flex items-start gap-3 py-1.5";

const sectionHeaderClass =
  "text-lg font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200";

const OnboardingFormContent: React.FC<OnboardingFormContentProps> = ({
  formData,
  onFormChange,
  onChildOrgChange,
  addChildOrg,
  removeChildOrg,
  onStakeholderChange,
  addStakeholder,
  removeStakeholder,
  logoFile,
  onContactChange,
  onPhaseToggle,
  onProductToggle,
  logoPreview,
  onLogoUpload,
  onRemoveLogo,
  cacFile,
  onCacUpload,
  dataMigrationFile,
  onDataMigrationUpload,
  companyProfileFile,
  onCompanyProfileUpload,
}) => {
  return (
    <div className="font-abel text-gray-700 text-sm leading-relaxed space-y-8 p-6 bg-white shadow-sm rounded-lg border border-gray-100 max-w-[1024px] mx-auto">
      {/* Header */}
      <div className="text-center mb-6 border-b pb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          25th Suite Onboarding Form and Pre Go Live Setup Checklist
        </h1>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-left max-w-lg mx-auto">
          <div>
            <label className={labelClass}>Client Name</label>
            <input
              type="text"
              value={formData.clientName}
              onChange={(e) => onFormChange("clientName", e.target.value)}
              placeholder="Customer Name"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Date Submitted</label>
            <input
              type="date"
              value={formData.dateSubmitted}
              onChange={(e) => onFormChange("dateSubmitted", e.target.value)}
              className={inputClass}
            />
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          The onboarding form is the entry point for every client. It captures
          all commercial, technical, and legal information needed to generate
          the MSA, create billing cards, and begin implementation.
        </p>
      </div>

      {/* Step A — Product bundle selector */}
      <section>
        <h2 className={sectionHeaderClass}>Step A — Product Bundle Selector</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>
              Products Selected <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2 mt-2">
              {["Direct TIP", "AI Recruiter", "Lifecycle"].map((product) => (
                <div key={product} className={checkboxRowClass}>
                  <input
                    type="checkbox"
                    checked={
                      formData.productsSelected?.includes(product) || false
                    }
                    onChange={() => onProductToggle(product)}
                    className="w-4 h-4 mt-0.5 accent-primary cursor-pointer"
                  />
                  <span>{product}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <label className={labelClass}>
                Tier Selected <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.tierSelected}
                onChange={(e) => onFormChange("tierSelected", e.target.value)}
                className={inputClass}
              >
                <option value="">Select Tier</option>
                <option value="Starter">Starter</option>
                <option value="Growth">Growth</option>
                <option value="Scale">Scale</option>
                <option value="Enterprise">Enterprise</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>
                Bundle Discount Applied (Auto-calc)
              </label>
              <input
                type="text"
                value={
                  (formData.productsSelected?.length || 0) >= 3
                    ? "12%"
                    : (formData.productsSelected?.length || 0) === 2
                      ? "8%"
                      : "0%"
                }
                readOnly
                className={`${inputClass} bg-gray-50`}
              />
              <p className="text-xs text-gray-500 mt-1">
                Applied to annual subscription only.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Step B — Three Contacts */}
      <section>
        <h2 className={sectionHeaderClass}>Step B — Key Contacts</h2>
        <div className="space-y-6">
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="font-medium text-gray-700">
                Company Details provided for pilot
              </p>
              <button
                onClick={addChildOrg}
                className="text-primary hover:text-primary/80 flex items-center gap-1 text-sm font-medium"
                type="button"
              >
                <span className="bg-primary/10 rounded-full w-5 h-5 flex items-center justify-center">
                  +
                </span>
                Add Company
              </button>
            </div>
            <div className="space-y-4">
              {formData.childOrganizations.map((org, i) => (
                <div
                  key={`org-${i}`}
                  className="bg-gray-50 p-4 rounded-lg border border-gray-200 relative"
                >
                  {formData.childOrganizations.length > 1 && (
                    <button
                      onClick={() => removeChildOrg(i)}
                      className="absolute -top-2 -right-2 bg-red-100 text-red-600 rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-200 transition-colors shadow-sm"
                      type="button"
                    >
                      -
                    </button>
                  )}
                  <h4 className="text-xs font-semibold text-gray-500 mb-1 uppercase">
                    {i === 0 ? "Parent Company" : `Sub Company ${i}`}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">
                        Company Name
                      </label>
                      <input
                        type="text"
                        value={org.name}
                        onChange={(e) =>
                          onChildOrgChange(i, "name", e.target.value)
                        }
                        placeholder={`e.g. Business A`}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">
                        Domain
                      </label>
                      <input
                        type="text"
                        value={org.domain}
                        onChange={(e) =>
                          onChildOrgChange(i, "domain", e.target.value)
                        }
                        placeholder={`e.g. business.com`}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">
                        Contact Email
                      </label>
                      <input
                        type="email"
                        value={org.contactEmail}
                        onChange={(e) =>
                          onChildOrgChange(i, "contactEmail", e.target.value)
                        }
                        placeholder={`e.g. admin@business.com`}
                        className={inputClass}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <section>
            <h2 className={sectionHeaderClass}>
              <div className="flex items-center justify-between">
                <div className="group relative left-0 flex items-center gap-2">
                  <div className="flex items-center gap-2">
                    Key Stakeholders and Decision
                    <Info className="w-4 h-4 text-gray-400 cursor-help" />
                  </div>
                  <div className="invisible group-hover:visible absolute left-48 top-12 -translate-y-1/2 ml-2 z-50 bg-white shadow-xl border border-gray-100 rounded-lg p-3 w-max min-w-[200px]">
                    <div className="grid grid-cols-1 gap-x-4 gap-y-2 text-sm">
                      <div className="text-gray-500 text-xs">
                        Access Level 0 is the highest level for business owner,
                        5 is the lowest
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  onClick={addStakeholder}
                  className="text-primary hover:text-primary/80 flex items-center gap-1 text-sm font-medium"
                  type="button"
                >
                  <span className="bg-primary/10 rounded-full w-5 h-5 flex items-center justify-center">
                    +
                  </span>
                  Add Stakeholder
                </button>
              </div>
            </h2>
            <div className="space-y-6">
              {formData.personnel.map((person, i) => (
                <div
                  key={`person-${i}`}
                  className="bg-gray-50 p-5 rounded-xl border border-gray-200 relative group/stakeholder"
                >
                  {formData.personnel.length > 1 && (
                    <button
                      onClick={() => removeStakeholder(i)}
                      className="absolute -top-2 -right-2 bg-red-100 text-red-600 rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-200 transition-colors shadow-sm opacity-0 group-hover/stakeholder:opacity-100"
                      type="button"
                    >
                      -
                    </button>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-start">
                    <div className="md:col-span-1">
                      <label className={labelClass}>Job Title</label>
                      <select
                        value={person.jobTitle}
                        onChange={(e) =>
                          onStakeholderChange(i, "jobTitle", e.target.value)
                        }
                        className={inputClass}
                      >
                        <option value="">Select Job Title</option>
                        <option value="CEO/Business Owner">
                          CEO/Business Owner
                        </option>
                        <option value="Finance / Billing">
                          Finance / Billing Contact
                        </option>
                        <option value="Project Manager">
                          Project Manager / Operational Contact{" "}
                        </option>
                        <option value="HR Lead">HR Lead</option>
                        <option value="IT Lead">IT Lead</option>
                        <option value="Data Protection Officer">
                          Data Protection Officer
                        </option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>First Name</label>
                      <input
                        type="text"
                        value={person.firstName}
                        onChange={(e) =>
                          onStakeholderChange(i, "firstName", e.target.value)
                        }
                        placeholder="First Name"
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Last Name</label>
                      <input
                        type="text"
                        value={person.lastName}
                        onChange={(e) =>
                          onStakeholderChange(i, "lastName", e.target.value)
                        }
                        placeholder="Last Name"
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Email Address</label>
                      <div className="flex">
                        <input
                          type="text"
                          value={person.email.split("@")[0]}
                          onChange={(e) => {
                            const prefix = e.target.value;
                            const domain =
                              formData.childOrganizations[0]?.domain ||
                              formData.parentDomain ||
                              "domain.com";
                            onStakeholderChange(
                              i,
                              "email",
                              `${prefix}@${domain}`,
                            );
                          }}
                          placeholder="e.g. john.doe"
                          className={`${inputClass} rounded-r-none border-r-0`}
                        />
                        <div className="bg-gray-100 px-3 flex items-center rounded-r-xl border border-gray-300 text-gray-500 text-sm whitespace-nowrap">
                          @
                          {formData.childOrganizations[0]?.domain ||
                            formData.parentDomain ||
                            "domain.com"}
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className={labelClass}>Access Level</label>
                      <select
                        value={person.accessLevel}
                        onChange={(e) =>
                          onStakeholderChange(
                            i,
                            "accessLevel",
                            parseInt(e.target.value),
                          )
                        }
                        className={inputClass}
                      >
                        <option value={0}>0</option>
                        <option value={1}>1</option>
                        <option value={2}>2</option>
                        <option value={3}>3</option>
                        <option value={4}>4</option>
                        <option value={5}>5</option>
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
          {/* L0 Contact */}
          {/* <div className="bg-gray-50 p-5 rounded-xl border border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">
              L0 Contact — CEO/Owner <span className="text-red-500">*</span>
            </h4>
            <p className="text-xs text-gray-500 mb-3">
              Primary authorised signatory. Receives all strategic and legal
              communications.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className={labelClass}>Name</label>
                <input
                  type="text"
                  value={formData.l0Contact?.name || ""}
                  onChange={(e) =>
                    onContactChange("l0Contact", "name", e.target.value)
                  }
                  placeholder="Full Name"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Email</label>
                <input
                  type="email"
                  value={formData.l0Contact?.email || ""}
                  onChange={(e) =>
                    onContactChange("l0Contact", "email", e.target.value)
                  }
                  placeholder="Email Address"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Phone</label>
                <input
                  type="tel"
                  value={formData.l0Contact?.phone || ""}
                  onChange={(e) =>
                    onContactChange("l0Contact", "phone", e.target.value)
                  }
                  placeholder="Phone Number"
                  className={inputClass}
                />
              </div>
            </div>
          </div> */}

          {/* Finance Contact */}
          {/* <div className="bg-gray-50 p-5 rounded-xl border border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">
              Finance / Billing Contact <span className="text-red-500">*</span>
            </h4>
            <p className="text-xs text-gray-500 mb-3">
              Invoice recipient. All invoices routed here. L0 copied.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className={labelClass}>Name</label>
                <input
                  type="text"
                  value={formData.financeContact?.name || ""}
                  onChange={(e) =>
                    onContactChange("financeContact", "name", e.target.value)
                  }
                  placeholder="Full Name"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Email</label>
                <input
                  type="email"
                  value={formData.financeContact?.email || ""}
                  onChange={(e) =>
                    onContactChange("financeContact", "email", e.target.value)
                  }
                  placeholder="Email Address"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Phone</label>
                <input
                  type="tel"
                  value={formData.financeContact?.phone || ""}
                  onChange={(e) =>
                    onContactChange("financeContact", "phone", e.target.value)
                  }
                  placeholder="Phone Number"
                  className={inputClass}
                />
              </div>
            </div>
          </div> */}

          {/* Project Manager Contact */}
          {/* <div className="bg-gray-50 p-5 rounded-xl border border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">
              Project Manager / Operational Contact{" "}
              <span className="text-red-500">*</span>
            </h4>
            <p className="text-xs text-gray-500 mb-3">
              Day-to-day implementation lead. Included in delay notifications
              and escalation Day 5.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className={labelClass}>Name</label>
                <input
                  type="text"
                  value={formData.projectManagerContact?.name || ""}
                  onChange={(e) =>
                    onContactChange(
                      "projectManagerContact",
                      "name",
                      e.target.value,
                    )
                  }
                  placeholder="Full Name"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Email</label>
                <input
                  type="email"
                  value={formData.projectManagerContact?.email || ""}
                  onChange={(e) =>
                    onContactChange(
                      "projectManagerContact",
                      "email",
                      e.target.value,
                    )
                  }
                  placeholder="Email Address"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Phone</label>
                <input
                  type="tel"
                  value={formData.projectManagerContact?.phone || ""}
                  onChange={(e) =>
                    onContactChange(
                      "projectManagerContact",
                      "phone",
                      e.target.value,
                    )
                  }
                  placeholder="Phone Number"
                  className={inputClass}
                />
              </div>
            </div>
          </div> */}
        </div>
      </section>

      {/* Step C — Company Details */}
      <section>
        <h2 className={sectionHeaderClass}>Step C — Company Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>
              Legal Entity Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.legalEntityName}
              onChange={(e) => onFormChange("legalEntityName", e.target.value)}
              placeholder="Exact registered name (Used on MSA)"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Brand Display Name</label>
            <input
              type="text"
              value={formData.brandDisplayName}
              onChange={(e) => onFormChange("brandDisplayName", e.target.value)}
              placeholder="Display Name"
              className={inputClass}
            />
          </div>
          <div className="md:col-span-2">
            <label className={labelClass}>
              Registered Address <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.headOfficeAddress}
              onChange={(e) =>
                onFormChange("headOfficeAddress", e.target.value)
              }
              placeholder="Full address (Used on MSA)"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>
              Registered Domain <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.parentDomain}
              onChange={(e) => onFormChange("parentDomain", e.target.value)}
              placeholder="e.g. company.com (For access control)"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>
              CAC Registration Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.cacRegNumber}
              onChange={(e) => onFormChange("cacRegNumber", e.target.value)}
              placeholder="Verified before go-live"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Industry or Sector</label>
            <input
              type="text"
              value={formData.industrySector}
              onChange={(e) => onFormChange("industrySector", e.target.value)}
              placeholder="e.g. Technology, Finance"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Jurisdiction Detected (Auto)</label>
            <input
              type="text"
              value={formData.jurisdictionDetected}
              readOnly
              className={`${inputClass} bg-gray-50`}
            />
            <p className="text-xs text-gray-500 mt-1">
              Determines governing law and currency.
            </p>
          </div>
        </div>
      </section>

      {/* Step D — Tier & Seat Configuration */}
      <section>
        <h2 className={sectionHeaderClass}>
          Step D — Tier & Seat Configuration
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>
              Number of named user seats <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={formData.namedUserSeats}
              onChange={(e) => onFormChange("namedUserSeats", e.target.value)}
              min="1"
              placeholder="Min = tier included seats"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Additional seats required</label>
            <input
              type="number"
              value={formData.additionalSeatsRequired}
              onChange={(e) =>
                onFormChange("additionalSeatsRequired", e.target.value)
              }
              min="0"
              placeholder="0 or more beyond included"
              className={inputClass}
            />
          </div>

          <div className="md:col-span-2 border-t pt-4 mt-2">
            <div className={checkboxRowClass}>
              <input
                type="checkbox"
                checked={formData.multiEntityFlag}
                onChange={(e) =>
                  onFormChange("multiEntityFlag", e.target.checked)
                }
                className="w-4 h-4 mt-0.5 accent-primary cursor-pointer"
              />
              <span className="font-medium">Multi-Entity Flag</span>
            </div>
            {formData.multiEntityFlag && (
              <div className="mt-3 pl-7">
                <label className={labelClass}>Number of Member Entities</label>
                <input
                  type="number"
                  value={formData.numberOfMemberEntities}
                  onChange={(e) =>
                    onFormChange("numberOfMemberEntities", e.target.value)
                  }
                  min="0"
                  placeholder="0 = Primary only"
                  className={inputClass}
                />
              </div>
            )}
          </div>

          {["Scale", "Enterprise"].includes(formData.tierSelected) && (
            <div className="md:col-span-2 border-t pt-4 mt-2">
              <div className={checkboxRowClass}>
                <input
                  type="checkbox"
                  checked={formData.concurrentLicensingRequired}
                  onChange={(e) =>
                    onFormChange(
                      "concurrentLicensingRequired",
                      e.target.checked,
                    )
                  }
                  className="w-4 h-4 mt-0.5 accent-primary cursor-pointer"
                />
                <span className="font-medium">
                  Concurrent licensing required (Scale/Enterprise only)
                </span>
              </div>
              {formData.concurrentLicensingRequired && (
                <div className="mt-3 pl-7">
                  <label className={labelClass}>
                    Number of concurrent sessions
                  </label>
                  <input
                    type="number"
                    value={formData.numberOfConcurrentSessions}
                    onChange={(e) =>
                      onFormChange("numberOfConcurrentSessions", e.target.value)
                    }
                    min="5"
                    placeholder="Min 5"
                    className={inputClass}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Priced at 1.5× additional seat rate.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Step E — Lifecycle Phases */}
      <section>
        <h2 className={sectionHeaderClass}>Step E — Lifecycle Phases</h2>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Phase 1 (Mandatory) */}
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 flex items-start gap-3">
              <input
                type="checkbox"
                checked={formData.lifecyclePhase1}
                readOnly
                className="w-4 h-4 mt-1 accent-primary cursor-not-allowed opacity-70"
              />
              <div>
                <span className="font-semibold text-gray-900">
                  Phase 1* — MVP Foundation
                </span>
                <p className="text-xs text-gray-500 mt-1">
                  Core HR infrastructure: employee records, org chart, document
                  vault, role management, admin dashboard, audit trail.
                </p>
              </div>
            </div>

            {/* Phases 2–7 */}
            {[
              {
                id: "Phase 2",
                title: "Phase 2 — Structured Onboarding",
                description:
                  "Digital onboarding, task checklists, new hire portal, e-signature, probation tracking.",
              },
              {
                id: "Phase 3",
                title: "Phase 3 — Employee Self-Service & Leave",
                description:
                  "Employee portal, leave application and approval, leave balances, manager dashboard.",
              },
              {
                id: "Phase 4",
                title: "Phase 4 — Performance & Probation",
                description:
                  "Goal setting, OKR tracking, review cycles, probation assessments, performance improvement plans.",
              },
              {
                id: "Phase 5",
                title: "Phase 5 — Smart Disciplinary & Policy",
                description:
                  "Disciplinary case management, policy library, grievance logging, HR audit trail.",
              },
              {
                id: "Phase 6",
                title: "Phase 6 — Offboarding & Exit",
                description:
                  "Resignation and termination workflows, asset retrieval, clearance sign-off, exit interviews.",
              },
              {
                id: "Phase 7",
                title: "Phase 7 — Offer Letter & Salary Workflow",
                description:
                  "Offer letter generation, compensation management, salary reviews, e-signature, payroll data push.",
              },
            ].map((phase) => (
              <div
                key={phase.id}
                className={`p-3 rounded-lg border flex items-start gap-3 transition-colors ${
                  formData.lifecyclePhases2to7?.includes(phase.id)
                    ? "bg-primary/5 border-primary/30"
                    : "bg-white border-gray-200 hover:bg-gray-50"
                }`}
              >
                <input
                  type="checkbox"
                  checked={
                    formData.lifecyclePhases2to7?.includes(phase.id) || false
                  }
                  onChange={() => onPhaseToggle(phase.id)}
                  className="w-4 h-4 mt-1 accent-primary cursor-pointer"
                />
                <div>
                  <span className="font-semibold text-gray-900">
                    {phase.title}
                  </span>
                  <p className="text-xs text-gray-500 mt-1">
                    {phase.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <label className={labelClass}>Phase 8+ Custom Requirements</label>
            <textarea
              value={formData.lifecyclePhase8Custom}
              onChange={(e) =>
                onFormChange("lifecyclePhase8Custom", e.target.value)
              }
              placeholder="Describe custom scope needed. Triggers custom scope request flow."
              rows={3}
              className={inputClass}
            />
          </div>
        </div>
      </section>

      {/* Step F — Billing */}
      {/* <section>
        <h2 className={sectionHeaderClass}>Step F — Billing</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className={labelClass}>
              Billing Model <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2 mt-2">
              {["Annual", "Quarterly", "Monthly"].map((model) => (
                <label
                  key={model}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <input
                    type="radio"
                    name="billingModel"
                    value={model}
                    checked={formData.billingModel === model}
                    onChange={(e) =>
                      onFormChange("billingModel", e.target.value)
                    }
                    className="accent-primary"
                  />
                  <span className="text-sm">
                    {model}{" "}
                    {model === "Monthly" && "(requires written agreement flag)"}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className={labelClass}>
              Payment Method <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2 mt-2">
              {["Bank Transfer", "Paystack", "PayPal"].map((method) => (
                <label
                  key={method}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <input
                    type="radio"
                    name="paymentMethod"
                    value={method}
                    checked={formData.paymentMethod === method}
                    onChange={(e) =>
                      onFormChange("paymentMethod", e.target.value)
                    }
                    className="accent-primary"
                  />
                  <span className="text-sm">{method}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className={labelClass}>
              Currency <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.currency}
              onChange={(e) => onFormChange("currency", e.target.value)}
              className={inputClass}
            >
              <option value="NGN">NGN (Default)</option>
              <option value="USD">
                USD (For Enterprise, requires written agreement)
              </option>
            </select>
          </div>
        </div>
      </section> */}

      {/* Step F — Uploads */}
      <section>
        <h2 className={sectionHeaderClass}>Step F — Document Uploads</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>
              CAC certificate / business registration{" "}
              <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="file"
                onChange={(e) =>
                  e.target.files?.[0] && onCacUpload(e.target.files[0])
                }
                className="hidden"
                id="cac-upload"
                accept=".pdf,.jpg,.jpeg,.png"
              />
              <label
                htmlFor="cac-upload"
                className={`${inputClass} cursor-pointer hover:bg-gray-50 flex items-center justify-center border-dashed border-2 bg-gray-50`}
              >
                {cacFile ? cacFile.name : "Upload CAC Doc (PDF or Image)"}
              </label>
            </div>
          </div>
          <div>
            <label className={labelClass}>
              Data migration files (if applicable)
            </label>
            <div className="relative">
              <input
                type="file"
                onChange={(e) =>
                  e.target.files?.[0] &&
                  onDataMigrationUpload(e.target.files[0])
                }
                className="hidden"
                id="data-migration-upload"
                accept=".csv,.xlsx,.xls"
              />
              <label
                htmlFor="data-migration-upload"
                className={`${inputClass} cursor-pointer hover:bg-gray-50 flex items-center justify-center border-dashed border-2 bg-gray-50`}
              >
                {dataMigrationFile
                  ? dataMigrationFile.name
                  : "Upload Migration File (CSV/Excel)"}
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Triggers data migration scope
            </p>
          </div>
        </div>
      </section>

      {/* Step G — Delivery Timeline */}
      <section>
        <h2 className={sectionHeaderClass}>Step G — Delivery Timeline</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>
              Target Go-Live Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.targetGoLiveDate}
              onChange={(e) => onFormChange("targetGoLiveDate", e.target.value)}
              className={inputClass}
            />
            <p className="text-xs text-gray-500 mt-1">
              Minimum 2 weeks from today.
            </p>
          </div>
          <div>
            <label className={labelClass}>
              Delivery Urgency <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2 mt-2">
              {["Standard", "Accelerated"].map((urgency) => (
                <label
                  key={urgency}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <input
                    type="radio"
                    name="deliveryUrgency"
                    value={urgency}
                    checked={formData.deliveryUrgency === urgency}
                    onChange={(e) =>
                      onFormChange("deliveryUrgency", e.target.value)
                    }
                    className="accent-primary"
                  />
                  <span className="text-sm">
                    {urgency}{" "}
                    {urgency === "Accelerated" && "(+25% on setup fees)"}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Step H — Pre-live checklist */}
      {/* <section>
        <h2 className={sectionHeaderClass}>Step H — Pre-Live Checklist</h2>
        <p className="text-xs text-gray-500 mb-4">
          This checklist is completed in the platform during implementation
          before go-live, not at submission.
        </p>
        <div className="space-y-3 bg-gray-50 p-5 rounded-xl border border-gray-200">
          <div className={checkboxRowClass}>
            <input
              type="checkbox"
              checked={formData.trainingCompletionRecord}
              readOnly
              className="w-4 h-4 mt-0.5 accent-primary opacity-50 cursor-not-allowed"
            />
            <div>
              <span className="font-medium text-gray-700">
                Training completion record
              </span>
            </div>
          </div>
          <div className={checkboxRowClass}>
            <input
              type="checkbox"
              checked={formData.uatSignOff}
              readOnly
              className="w-4 h-4 mt-0.5 accent-primary opacity-50 cursor-not-allowed"
            />
            <div>
              <span className="font-medium text-gray-700">UAT sign-off</span>
            </div>
          </div>
          <div className={checkboxRowClass}>
            <input
              type="checkbox"
              checked={formData.dataMigrationVerified}
              readOnly
              className="w-4 h-4 mt-0.5 accent-primary opacity-50 cursor-not-allowed"
            />
            <div>
              <span className="font-medium text-gray-700">
                Data migration verified
              </span>
            </div>
          </div>
          <div className={checkboxRowClass}>
            <input
              type="checkbox"
              checked={formData.integrationsTested}
              readOnly
              className="w-4 h-4 mt-0.5 accent-primary opacity-50 cursor-not-allowed"
            />
            <div>
              <span className="font-medium text-gray-700">
                Integrations tested
              </span>
            </div>
          </div>
          <div className={checkboxRowClass}>
            <input
              type="checkbox"
              checked={formData.clientL0GoLiveReadinessDeclaration}
              readOnly
              className="w-4 h-4 mt-0.5 accent-primary opacity-50 cursor-not-allowed"
            />
            <div>
              <span className="font-medium text-gray-700">
                Client L0 go-live readiness declaration
              </span>
            </div>
          </div>
        </div>
      </section> */}

      {/* Section 10 — Signature & Submit */}
      <section>
        <h2 className={sectionHeaderClass}>Section 10 — Signature & Submit</h2>
        <div className="bg-gray-50 p-5 rounded-xl border border-gray-200">
          <div className={`${checkboxRowClass} mb-4`}>
            <input
              type="checkbox"
              checked={formData.electronicAgreement}
              onChange={(e) =>
                onFormChange("electronicAgreement", e.target.checked)
              }
              className="w-4 h-4 mt-0.5 accent-primary cursor-pointer"
            />
            <span className="font-medium text-gray-900">
              I agree to sign this document electronically and confirm that all
              information provided is accurate.{" "}
              <span className="text-red-500">*</span>
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>
                Authorized Signatory Name{" "}
                <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.authorizedFullName}
                onChange={(e) =>
                  onFormChange("authorizedFullName", e.target.value)
                }
                placeholder="Full Name"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>
                Authorized Signatory Title{" "}
                <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.authorizedTitle}
                onChange={(e) =>
                  onFormChange("authorizedTitle", e.target.value)
                }
                placeholder="Job Title"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Date</label>
              <input
                type="date"
                value={formData.authorizedDate}
                readOnly
                className={`${inputClass} bg-gray-100 text-gray-500`}
              />
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-gray-200 flex flex-col md:flex-row justify-between items-center text-xs text-gray-500">
            <div className="flex items-center gap-1.5 text-gray-600">
              <span className="inline-block w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
              <span>
                Jurisdiction:{" "}
                <span className="font-semibold text-gray-800">
                  {formData.jurisdictionDetected || "Detecting..."}
                </span>
              </span>
            </div>
            <div className="mt-2 md:mt-0 text-right">
              <p>
                Submission Timestamp: {new Date().toLocaleDateString()}{" "}
                {new Date().toLocaleTimeString()}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OnboardingFormContent;
